import React, { useCallback, useEffect, useState } from "react";
import { Content } from "antd/es/layout/layout";
import {
  SearchOutlined,
  PlusOutlined,
  RightOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Input, List } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getTableID } from "../Services/authServices";
import NewRecipientSetupModal from "../Components/NewRecipientSetupModal";
import canadaFlag from "../Image/ca-circle.png";
import barbadosFlag from "../Image/flag/barbados.png";
import "./style.css";

const getAllowedDeliveryMethods = (senderCurrency, recipientCurrency) => {
  if (senderCurrency === "CAD" && recipientCurrency === "BBD") {
    return ["Bank Transfer", "BajCan Wallet", "Cash Pickup"];
  }
  return ["BajCan Wallet"];
};

const getRecipientCurrency = (recipientCountry) => {
  const normalized = String(recipientCountry || "")
    .trim()
    .toLowerCase();

  if (normalized === "canada") return "CAD";
  if (normalized === "barbados") return "BBD";

  return "";
};

const SelectRecipient = () => {
  const API = generateClient();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [recipients, setRecipients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [senderWalletCurrency, setSenderWalletCurrency] = useState("CAD");
  const [showNewRecipientModal, setShowNewRecipientModal] = useState(false);
  const isSendMoneyFlow = state?.flow === "sendMoney";
  const sendDraft = state?.sendDraft;
  const selectedTargetCurrency = String(
    sendDraft?.targetCurrency || "",
  ).toUpperCase();
  const fetchRecipients = useCallback(async () => {
    try {
      const query = `
        query GetRecipientsByUser($id: ID!) {
          getUser(id: $id) {
            Wallets(limit: 1) {
              items {
                currency
              }
            }
            Recipients {
              items {
                id
                firstName
                lastName
                email
                phoneNumber
                typeOfRecipient
                businessName
                recipientDeliveryMethod
                recipientWalletID
                RecipientWallet {
                  currency
                }
                addressStreet
                addressCity
                addressState
                addressCountry
                addressZipCode
                bankName
                bankBranch
                bankAccountHolderName
                bankAccountNumber
              }
            }
          }
        }
      `;
      const tableID = await getTableID();
      if (!tableID) {
        console.error("Table ID is not available.");
        return;
      }

      const userData = await API.graphql({
        query,
        variables: { id: tableID },
      });
      const walletCurrency =
        userData?.data?.getUser?.Wallets?.items?.[0]?.currency || "CAD";
      setSenderWalletCurrency(String(walletCurrency).toUpperCase());
      setRecipients(userData?.data?.getUser?.Recipients?.items || []);
    } catch (err) {
      console.error("Error fetching recipients:", err);
    }
  }, [API]);

  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);

  const getRecipientCurrencyCode = (recipient) => {
    const recipientCountryCurrency = getRecipientCurrency(
      recipient?.addressCountry,
    );
    if (recipientCountryCurrency) {
      return recipientCountryCurrency;
    }

    const walletCurrency = String(
      recipient?.RecipientWallet?.currency || "",
    ).toUpperCase();
    if (walletCurrency === "CAD" || walletCurrency === "BBD") {
      return walletCurrency;
    }
    return "BBD";
  };

  const filteredRecipients = recipients
    .filter((recipient) => {
      const firstName = (recipient.firstName || "").toLowerCase();
      const lastName = (recipient.lastName || "").toLowerCase();
      const search = searchTerm.toLowerCase();
      return firstName.includes(search) || lastName.includes(search);
    })
    .filter((recipient) => {
      if (!isSendMoneyFlow) return true;

      const recipientCurrency = getRecipientCurrencyCode(recipient);
      const allowedMethods = getAllowedDeliveryMethods(
        senderWalletCurrency,
        recipientCurrency,
      );

      const isRouteSupported =
        (senderWalletCurrency === "CAD" && recipientCurrency === "CAD") ||
        (senderWalletCurrency === "CAD" && recipientCurrency === "BBD") ||
        (senderWalletCurrency === "BBD" && recipientCurrency === "CAD") ||
        (senderWalletCurrency === "BBD" && recipientCurrency === "BBD");

      const matchesTargetCurrency = selectedTargetCurrency
        ? recipientCurrency === selectedTargetCurrency
        : true;

      return (
        isRouteSupported &&
        matchesTargetCurrency &&
        allowedMethods.includes(recipient?.recipientDeliveryMethod || "")
      );
    })
    .sort((a, b) => {
      const aName = `${a?.firstName || ""} ${a?.lastName || ""}`
        .trim()
        .toLowerCase();
      const bName = `${b?.firstName || ""} ${b?.lastName || ""}`
        .trim()
        .toLowerCase();
      return aName.localeCompare(bName);
    });

  const formatPhoneNumber = (value) => {
    const digits = (value || "").replace(/\D/g, "");
    if (!digits) return "No phone number";

    if (digits.startsWith("1246") && digits.length === 11) {
      const local = digits.slice(4);
      return `+1 246 ${local.slice(0, 3)}-${local.slice(3)}`;
    }

    if (digits.startsWith("1") && digits.length === 11) {
      const local = digits.slice(1);
      return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
    }

    if (digits.length === 7) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }

    return value;
  };

  const getCurrencyFlag = (currencyCode) =>
    currencyCode === "CAD" ? canadaFlag : barbadosFlag;

  const handleNewRecipientClick = () => {
    if (isSendMoneyFlow) {
      setShowNewRecipientModal(true);
      return;
    }
    setShowNewRecipientModal(true);
  };

  const handleNewRecipientSetupComplete = ({
    recipientAdded,
    recipientCurrency,
    recipientDeliveryMethod,
  }) => {
    if (recipientAdded) {
      setShowNewRecipientModal(false);
      fetchRecipients();
      return;
    }

    navigate("/addRecipient", {
      state: {
        flow: isSendMoneyFlow ? "sendMoney" : "recipientManagement",
        sendDraft: isSendMoneyFlow ? sendDraft : undefined,
        recipientCurrency,
        recipientDeliveryMethod,
      },
    });
    setShowNewRecipientModal(false);
  };

  return (
    <Content className="appPageContent">
      <div
        className={`appPageShell ${
          isSendMoneyFlow ? "appPageShellNarrow" : "recipientPageShell"
        }`}
      >
        <div
          className={`appSectionHeader ${
            isSendMoneyFlow ? "appPageHeader recipientListHeader" : "recipientListHeader"
          }`}
        >
          {isSendMoneyFlow ? (
            <Button
              type="text"
              icon={<LeftOutlined />}
              onClick={() => navigate("/dashboard")}
              className="backButton"
            />
          ) : null}
          <h2 className="appSectionTitle">
            {isSendMoneyFlow ? "Select Recipient" : "Recipients"}
          </h2>
        </div>

        <div className="recipientActionsRow recipientActionsTopRow">
          <Button
            icon={<PlusOutlined />}
            className="recipientInlineAction appSecondaryBtn"
            size="large"
            onClick={handleNewRecipientClick}
          >
            New Recipient
          </Button>
          <Input
            allowClear
            className="appSearchInput"
            size="large"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined style={{ color: "#00267F" }} />}
            placeholder="Search Recipient"
          />
        </div>

        <div className="recipientListWrap">
          {isSendMoneyFlow && selectedTargetCurrency && (
            <p className="recipientFlowHelperText">
              {selectedTargetCurrency === "CAD"
                ? "Showing recipients in Canada"
                : "Showing recipients in Barbados"}
            </p>
          )}
          <List
            size="large"
            bordered={false}
            dataSource={filteredRecipients}
            className="appListCard recipientListCard"
            locale={{
              emptyText: isSendMoneyFlow
                ? selectedTargetCurrency === "CAD"
                  ? "No recipients in Canada"
                  : "No recipients in Barbados"
                : "No recipients found.",
            }}
            renderItem={(recipient) => {
              const recipientCurrency = getRecipientCurrencyCode(recipient);
              return (
                <List.Item
                  className="recipientListItem"
                  onClick={() => {
                    if (isSendMoneyFlow) {
                      navigate("/sendMoney", {
                        state: {
                          recipient,
                          from: "recipientSelection",
                          ...sendDraft,
                        },
                      });
                      return;
                    }
                    navigate("/recipientDetail", { state: { recipient } });
                  }}
                >
                  <div className="recipientAvatarWrap">
                    <Avatar
                      size={60}
                      src={`https://api.dicebear.com/7.x/initials/svg?seed=${recipient.firstName} ${recipient.lastName}`}
                      className="recipientAvatar"
                    />
                    <span
                      className="recipientCurrencyBubble"
                      title={recipientCurrency}
                    >
                      <img
                        src={getCurrencyFlag(recipientCurrency)}
                        alt={recipientCurrency}
                        className="recipientCurrencyBubbleFlag"
                      />
                    </span>
                  </div>
                  <div className="recipientItemBody">
                    <div className="recipientItemTopRow">
                      <div className="recipientItemName">
                        {`${recipient.firstName} ${recipient.lastName}`}
                      </div>
                      {recipient.typeOfRecipient && (
                        <span className="recipientTypePill">
                          {recipient.typeOfRecipient}
                        </span>
                      )}
                    </div>
                    <div className="recipientItemMeta">
                      {recipient.email || "No email"}
                    </div>
                    {recipient.recipientDeliveryMethod ? (
                      <div className="recipientItemMeta recipientItemMethod">
                        {recipient.recipientDeliveryMethod === "Bank Transfer" &&
                        recipient.bankName
                          ? `Bank • ${recipient.bankName}`
                          : recipient.recipientDeliveryMethod}
                      </div>
                    ) : null}
                  </div>
                  <RightOutlined className="recipientItemArrow" />
                </List.Item>
              );
            }}
          />
        </div>
      </div>

      <NewRecipientSetupModal
        open={showNewRecipientModal}
        onCancel={() => setShowNewRecipientModal(false)}
        senderWalletCurrency={senderWalletCurrency}
        presetRecipientCurrency={
          isSendMoneyFlow ? selectedTargetCurrency || undefined : undefined
        }
        onComplete={handleNewRecipientSetupComplete}
      />
    </Content>
  );
};

export default SelectRecipient;
